import { combineEpics, Epic, ofType } from 'redux-observable';
import { EMPTY, forkJoin, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { getType } from 'typesafe-actions';

import { EpicDeps } from '../../../service/StoreFactory';
import { State } from '../../../typings/state';
import { CanvasAction } from '../../common/actions';
import { CommonCreativeIdeaApiResponse, DisclaimersApiResponse } from '../../common/requestsTypings';
import { openModal } from '../../CreativeSmartCenterModal/actions';
import { setEditorData, smartCenter } from '../../FileEditor/actions';
import {
    AddCreativeAction,
    applySmartCenterSuccess,
    initAdaptiveCreative,
    initAdaptiveCreativeSuccess,
    resetPreviewsModal,
    setSmartCenter
} from '../actions';
import { isCommonOptionsAvailable } from '../AddCommonCreative/helpers';
import { showAddCreativeError, showAddCreativeErrorAction } from '../epics';
import { getCroppableOptionsEditorIds } from '../helpers';

import { getAdaptiveCreativeState, getSmartCentersValueFromPreview } from './helpers';
import {
    mapAdaptiveCreativeFromApi,
    mapAdaptiveCreativeFromIdeaApi,
    mapAdaptiveCreativeFromPresetApi
} from './mappers';

const initDataEpic: Epic<AddCreativeAction, AddCreativeAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(initAdaptiveCreative)),
        switchMap(
            (): Observable<AddCreativeAction> => {
                const {
                    sharedData,
                    canvas: {
                        commonData: { ideaId },
                        addCreative
                    }
                } = store$.value.canvasEditor;
                const tld = store$.value.commonData.tld;
                const { presetId, id } = addCreative;
                const api = deps.canvasApi.addCommonCreative;
                const commonOptionsAvailable = isCommonOptionsAvailable(sharedData.creativeType);

                if (ideaId && presetId) {
                    return forkJoin(
                        api.fetchIdeaData(ideaId, presetId, sharedData),
                        api.fetchDisclaimers(sharedData)
                    ).pipe(
                        map(([preset, disclaimers]: [CommonCreativeIdeaApiResponse, DisclaimersApiResponse]) =>
                            mapAdaptiveCreativeFromIdeaApi(preset, disclaimers, tld, commonOptionsAvailable)
                        ),
                        map(initAdaptiveCreativeSuccess),
                        catchError(error => showAddCreativeError(error.message || error.getNativeError().message))
                    );
                }

                if (id) {
                    return forkJoin(api.fetchBatchData(id, sharedData), api.fetchDisclaimers(sharedData)).pipe(
                        map(([batch, disclaimers]) =>
                            mapAdaptiveCreativeFromApi(batch, disclaimers, tld, commonOptionsAvailable)
                        ),
                        map(initAdaptiveCreativeSuccess),
                        catchError(error => showAddCreativeError(error.message || error.getNativeError().message))
                    );
                }

                if (presetId) {
                    return forkJoin(
                        api.fetchPresetData(Number(presetId), sharedData),
                        api.fetchDisclaimers(sharedData)
                    ).pipe(
                        map(([preset, disclaimers]) =>
                            mapAdaptiveCreativeFromPresetApi(preset, disclaimers, tld, commonOptionsAvailable)
                        ),
                        map(initAdaptiveCreativeSuccess),
                        catchError(error => showAddCreativeError(error.message || error.getNativeError().message))
                    );
                }

                return showAddCreativeError();
            }
        )
    );
};

const smartCenterEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(setSmartCenter)),
        map(() => {
            const addCreative = getAdaptiveCreativeState(store$.value.canvasEditor.canvas.addCreative);
            if (!addCreative) {
                return showAddCreativeErrorAction();
            }

            const { item, previewItems, editingFileInfo, creativeOptions } = addCreative;

            if (!item || !previewItems || !creativeOptions) {
                return showAddCreativeErrorAction();
            }

            // если никакой новый файл мы сейчас не редактируем
            if (!editingFileInfo) {
                const editorIds = getCroppableOptionsEditorIds(creativeOptions, true);

                return smartCenter(editorIds[0]);
            }

            return openModal(
                {
                    src: editingFileInfo.cropSourceUrl,
                    resourceId: editingFileInfo.fileId
                },
                editingFileInfo.origSrc,
                getSmartCentersValueFromPreview(previewItems[0], editingFileInfo.mediaSetId)
            );
        })
    );
};

const smartCenterSuccessEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(applySmartCenterSuccess)),
        switchMap((): Observable<CanvasAction> | CanvasAction[] => {
            const addCreative = getAdaptiveCreativeState(store$.value.canvasEditor.canvas.addCreative);
            if (!addCreative) {
                return showAddCreativeError();
            }
            const { editingFileInfo, previewsModalVisible } = addCreative;

            if (previewsModalVisible) {
                return EMPTY;
            }

            const outputActions: CanvasAction[] = [resetPreviewsModal()];

            if (editingFileInfo && editingFileInfo.preview) {
                outputActions.push(setEditorData(editingFileInfo));
            }

            return outputActions;
        })
    );
};

export const addAdaptiveCreativeEpics = combineEpics(initDataEpic, smartCenterEpic, smartCenterSuccessEpic);
